import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { fetchResearchLibrarySummary } from '../services/researchLibraryService';

const REFRESH_MS = 3 * 60 * 60 * 1000;
const HIDE_KEY = 'researchLibraryBanner:hiddenOn';
const today = () => new Date().toISOString().slice(0, 10);

function hiddenToday() {
  try {
    return window.localStorage.getItem(HIDE_KEY) === today();
  } catch (e) {
    return false;
  }
}

/**
 * The maintainers' banner (owner, Oct 15 2026): while any research record is
 * more than a month old, super admins see how many and open Admin → Portfolios
 * from here. It can be hidden for the day.
 */
export default function ResearchLibraryBanner({ enabled, onOpen }) {
  const [summary, setSummary] = useState(null);
  const [hidden, setHidden] = useState(hiddenToday);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const load = () => fetchResearchLibrarySummary()
      .then((data) => { if (!cancelled) setSummary(data); })
      .catch(() => {});
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [enabled]);

  if (!enabled || hidden || !summary || !summary.counts.needsUpdate) return null;
  const { needsUpdate, usedAndNeedsUpdate } = summary.counts;
  const hide = () => {
    try { window.localStorage.setItem(HIDE_KEY, today()); } catch (e) { /* the banner just stays */ }
    setHidden(true);
  };

  return (
    <div className="px-4 pb-1.5">
      <div role="status" className="flex flex-wrap items-center gap-2 rounded-md border border-amber-400/60 bg-amber-50 px-3 py-1.5 text-xs text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          {needsUpdate} research record{needsUpdate === 1 ? ' is' : 's are'} more than a month old
          {usedAndNeedsUpdate ? ` (${usedAndNeedsUpdate} used by live clients)` : ''}.
        </span>
        <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={onOpen}>Update research</Button>
        <Button type="button" size="icon" variant="ghost" className="h-6 w-6" onClick={hide} aria-label="Hide for today">
          <X className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </div>
    </div>
  );
}
